import { Inject, Injectable, Signal } from '@angular/core';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { Identity, IdentityService } from 'src/app/core/identity.service';
import { FeatureManagementFlag } from 'src/app/shared/models/feature-management/feature-management-flag';
import * as LDClient from 'launchdarkly-js-client-sdk';
import { filter, from, map, Observable, of, ReplaySubject, switchMap, tap } from 'rxjs';
import { LaunchDarklyService } from 'src/app/shared/services/ld.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { LoggerService } from './logger.service';

@Injectable({
    providedIn: 'root'
})
export class LDFeatureManager {
    private _ldClient!: LDClient.LDClient;
    private _cachedFlags: LDClient.LDFlagSet = {};
    private readonly _flags$: ReplaySubject<FlagsState> = new ReplaySubject<FlagsState>(1);
    private _contextSwitchDepth = 0;
    private _instantiated = false;

    constructor(
        @Inject(APP_CONFIG) readonly settings: Settings,
        private readonly launchDarklyService: LaunchDarklyService,
        private readonly identityService: IdentityService,
        private readonly loggerService: LoggerService
    ) {}

    public async init() {
        if (!this._instantiated) {
            this._instantiated = true;

            this._ldClient = this.launchDarklyService.createClient(
                this.settings.launchDarklyClientSdkKey,
                this.createUser(this.identityService.current)
            );

            this._ldClient.on('error', (err: unknown) => {
                const errorMessage = `LaunchDarkly init error${err instanceof Error ? ': ' + err.message : '.'}`;
                this.loggerService.error(errorMessage, err as Record<string, any>);
            });
            this._ldClient.on('failed', (err: unknown) => {
                const errorMessage = `LaunchDarkly init error${err instanceof Error ? ': ' + err.message : '.'}`;
                this.loggerService.error(errorMessage, err as Record<string, any>);
            });

            await this._ldClient.waitUntilReady();
            await this.changeUser(this.identityService.current);
            this.loadFlags();
            this._ldClient.on('change', (flags) => {
                this.updateFlags(flags);
            });
        }
    }

    public isEnabled(featureName: FeatureFlag): Observable<boolean> {
        let isFirstLoad = true;

        return this._flags$.pipe(
            filter((flags) => isFirstLoad || flags.changedFlags.includes(featureName)),
            tap(() => (isFirstLoad = false)),
            map((flags) => {
                const value = flags.flagsState[featureName];

                if (typeof value === 'boolean') {
                    return value;
                } else {
                    const errorMessage = `Feature flag "${featureName}" was not found or is not a boolean.`;
                    console.error(errorMessage);
                    this.loggerService.error(errorMessage);
                    return false;
                }
            })
        );
    }

    isEnabledSignal(featureName: FeatureFlag, initialValue = false): Signal<boolean> {
        return toSignal(this.isEnabled(featureName), { initialValue });
    }

    public isDisabled(featureName: FeatureFlag): Observable<boolean> {
        let isFirstLoad = true;
        return this._flags$.pipe(
            filter((flags) => typeof flags.flagsState[featureName] === 'boolean'),
            filter((flags) => isFirstLoad || flags.changedFlags.includes(featureName)),
            tap(() => (isFirstLoad = false)),
            map((flags) => !flags.flagsState[featureName])
        );
    }

    public getNumberFeatureFlag(featureName: FeatureFlag): Observable<number> {
        let isFirstLoad = true;
        return this._flags$.pipe(
            filter((flags) => isFirstLoad || flags.changedFlags.includes(featureName)),
            tap(() => (isFirstLoad = false)),
            map((flags) => flags.flagsState[featureName])
        );
    }

    public getJsonFeatureFlag<T>(featureName: FeatureFlag): Observable<T> {
        let isFirstLoad = true;
        return this._flags$.pipe(
            filter((flags) => isFirstLoad || flags.changedFlags.includes(featureName)),
            tap(() => (isFirstLoad = false)),
            map((flags) => flags.flagsState[featureName])
        );
    }

    public selectFeatureFlag(featureName: FeatureFlag): Observable<FeatureManagementFlag> {
        let isFirstLoad = true;
        return this._flags$.pipe(
            filter((flags) => isFirstLoad || flags.changedFlags.includes(featureName)),
            tap(() => (isFirstLoad = false)),
            map((flags) => ({
                enabled: flags.flagsState[featureName] === true,
                value: flags.flagsState[featureName],
                flagName: featureName
            }))
        );
    }

    public getUser(): LDClient.LDContext {
        return this._ldClient.getContext();
    }

    // After identify() completes, emit a full flag refresh so signal/observable consumers
    // immediately see values for the new user context.
    public async changeUser(identityUser?: Identity): Promise<LDClient.LDFlagSet> {
        const user = this.createUser(identityUser);
        const flags = await this._ldClient.identify(user);

        this._cachedFlags = flags ?? {};
        this._flags$.next({
            flagsState: this._cachedFlags,
            changedFlags: Object.values(FeatureFlag)
        });

        return flags;
    }

    public dispose(): Observable<any> {
        return from(this._ldClient.close());
    }

    private loadFlags(): void {
        this._cachedFlags = this._ldClient.allFlags();
        const allFlagNames = Object.values(FeatureFlag);
        this._flags$.next({ flagsState: this._cachedFlags, changedFlags: allFlagNames });
    }

    private updateFlags(flags: LDClient.LDFlagChangeset): void {
        // Temporary facility/multi-context evaluations call identify() as well. Ignore the SDK
        // change event during those swaps so we do not re-emit flag updates and re-enter callers.
        if (this._contextSwitchDepth > 0) {
            return;
        }

        if (this._cachedFlags !== undefined && this._cachedFlags !== null) {
            const updatedFlags: string[] = [];
            Object.values(FeatureFlag).forEach((flagName) => {
                if (flags[flagName] !== undefined) {
                    this._cachedFlags[flagName] = flags[flagName].current;
                    if (flags[flagName].current !== flags[flagName].previous) {
                        updatedFlags.push(flagName);
                    }
                }
            });
            if (updatedFlags.length !== 0) {
                this._flags$.next({ flagsState: this._cachedFlags, changedFlags: updatedFlags });
            }
        }
    }

    private createUser(identityUser: Identity | undefined): LDClient.LDContext {
        if (identityUser?.userId) {
            const user = {
                key: identityUser.userId,
                name: identityUser.userName,
                email: identityUser.email,
                firstName: identityUser.firstName,
                lastName: identityUser.lastName,
                anonymous: false
            };
            return user;
        } else {
            return { key: 'anonymous', anonymous: true };
        }
    }

    /**
     * @summary Checks if Nova Physical Units feature is enabled for a specific facility.
     * This method first verifies if the child feature flag is enabled in the current context,
     * and only if it is, proceeds to check the parent flag in the facility context.
     * This approach is safe because:
     * 1. We check the child flag first to avoid unnecessary context switching
     * 2. The facility context is only changed if the child flag is enabled
     * 3. The parent flag is read directly from the identify() response
     * 4. The context is always reset back to the user context before notifying consumers
     * 5. Both flags must be enabled for the feature to be considered active
     *
     * @param facilityId - The ID of the facility to check the feature for
     * @param childFlag - The child feature flag to check
     * @returns Observable<boolean> - true if both parent and child flags are enabled
     */
    public isNovaPhysicalUnitsEnabled(facilityId: string, childFlag: FeatureFlag): Observable<boolean> {
        return this.isEnabled(childFlag).pipe(
            switchMap((isChildFlagEnabled) => {
                if (!isChildFlagEnabled) {
                    return of(false);
                }

                // Evaluate the parent flag from the identify() response instead of subscribing to
                // _flags$ while the client is in facility context.
                return from(
                    this.withTemporaryContext(
                        () => this.changeFacilityContext(facilityId),
                        (flags) => this.getBooleanFlagValue(flags, FeatureFlag.UnitVersePhysicalUnitParent)
                    )
                );
            })
        );
    }

    public isCostCenterForFinanceEnabled(facilityId: string): Observable<boolean> {
        return this.evaluateFlagForFacility(
            facilityId,
            FeatureFlag.EnableCostCentersFinance,
            'evaluating cost center feature'
        );
    }

    /**
     * @summary Changes the facility context in LaunchDarkly.
     * This method switches the context to a specific facility for flag evaluation.
     * @param facilityId - The ID of the facility to switch to
     * @returns Promise<LDClient.LDFlagSet> - The updated flag set after context change
     */
    public changeFacilityContext(facilityId: string): Promise<LDClient.LDFlagSet> {
        const context = this.createFacilityContext(facilityId);
        return this._ldClient.identify(context);
    }

    private createFacilityContext(facilityId: string): LDClient.LDContext {
        return {
            kind: 'facility',
            key: facilityId,
            attributes: {
                facilityId: facilityId
            }
        };
    }

    /**
     * @summary Evaluates a feature flag for a specific facility using multi-context.
     * This helper method handles the common pattern of:
     * 1. Switching to multi-context (user + facility)
     * 2. Reading flag value from identify() response
     * 3. Resetting back to user context
     * 4. Error handling with context reset
     *
     * @param facilityId - The ID of the facility to evaluate the flag for
     * @param featureName - The feature flag to evaluate
     * @param errorContext - Description for error logging
     * @returns Observable<boolean> - The flag value for the facility
     */
    private evaluateFlagForFacility(
        facilityId: string,
        featureName: FeatureFlag,
        errorContext: string
    ): Observable<boolean> {
        return from(
            this.withTemporaryContext(
                () => this._ldClient.identify(this.getMultiContext(facilityId)),
                (flags) => this.getBooleanFlagValue(flags, featureName)
            ).catch((error) => {
                this.loggerService.error(`Error ${errorContext} for facility ${facilityId}`, error);
                throw error;
            })
        );
    }

    private getBooleanFlagValue(flags: LDClient.LDFlagSet, featureName: FeatureFlag): boolean {
        const flagValue = flags[featureName];
        return typeof flagValue === 'boolean' ? flagValue : false;
    }

    private async restoreUserContext(): Promise<LDClient.LDFlagSet> {
        // Restoring after a temporary evaluation must refresh the cached user-scoped flags without
        // notifying subscribers. Actual user changes still flow through changeUser().
        const flags = await this._ldClient.identify(this.createUser(this.identityService.current));
        this._cachedFlags = flags ?? {};

        return this._cachedFlags;
    }

    private async withTemporaryContext<T>(
        identifyContext: () => Promise<LDClient.LDFlagSet>,
        readFlags: (flags: LDClient.LDFlagSet) => T | Promise<T>
    ): Promise<T> {
        // Track temporary context depth so nested evaluations can share the same suppression guard.
        this._contextSwitchDepth += 1;

        try {
            const flags = await identifyContext();
            return await readFlags(flags ?? {});
        } finally {
            try {
                await this.restoreUserContext();
            } finally {
                this._contextSwitchDepth = Math.max(0, this._contextSwitchDepth - 1);
            }
        }
    }

    private getMultiContext(facilityId: string): LDClient.LDMultiKindContext {
        const user = this.createUser(this.identityService?.current);
        const facility = this.createFacilityContext(facilityId);

        return { kind: 'multi', user, facility };
    }
}

interface FlagsState {
    flagsState: LDClient.LDFlagSet;
    changedFlags: string[];
}
